import React, { Suspense, useEffect, useState } from 'react';
import { BrowserRouter, Routes, Route } from 'react-router-dom';
import { logLevels, logEvents } from './constants/appConstants';
import logger from './services/logger';

import { initI18n } from './i18n';
import i18next from 'i18next';
import { I18nextProvider } from 'react-i18next';

import NotFound from './components/NotFound';

const Trial = React.lazy(() => import('./components/Trial'));
const Sso = React.lazy(() => import('./components/Sso'));
const ErrorPage = React.lazy(() => import('./components/Error'));
const Init = React.lazy(() => import('./components/Init'));
const Plans = React.lazy(() => import('./components/Plans'));
const ConnectOauth = React.lazy(() => import('./components/ConnectOauth'));
const Root = React.lazy(() => import('./containers/Root'));
const PlansAntivirus = React.lazy(() => import('./components/PlansAntivirus'));
const PlansOptimizer = React.lazy(() => import('./components/PlansOptimizer'));
const PlansVpn = React.lazy(() => import('./components/PlansVpn'));
const PlansUpsell = React.lazy(() => import('./components/PlansUpsell'));
const PlansTrial = React.lazy(() => import('./components/PlansTrial'));
const Upsell = React.lazy(() => import('./components/Upsell'));
const Fallback = React.lazy(() => import('./components/Fallback'));
const OneClickPayment = React.lazy(() => import('./components/OneClickPayment'));
const Onboarding = React.lazy(() => import('./components/Onboarding'));
const Faq = React.lazy(() => import('./components/Faq'));
const Toaster = React.lazy(() => import('./components/Toaster/Toaster'));
const Interstitial = React.lazy(() => import('./components/Interstitial'));
const IdentityAssistant = React.lazy(() => import('./components/IdentityAssistant'));
const Store = React.lazy(() => import('./components/Store'));
const RegistrationOffer = React.lazy(() => import('./components/RegistrationOffer'));

import DataProvider from './containers/DataProvider';
import './styles/index.less';


function RootApp() {
    const [i18nLoaded, setI18nLoaded] = useState(false);
    const [i18nError, setI18nError] = useState(null);

    useEffect(() => {
        const pathParts = window.location.pathname.split('/');
        const possibleLang = pathParts[1];
        const initialLang = (possibleLang && possibleLang.length >= 2 && possibleLang.length <= 5)
            ? possibleLang
            : 'en';
        const initialDebugMode = process.env.NODE_ENV === 'development';

        initI18n(initialLang, initialDebugMode, null)
            .then(() => {
                logger(logLevels.DEBUG, { source: 'RootApp.js', action: logEvents.EXTERNAL_EVENT_CALLED, payload: 'i18n ready, rendering app.' });
                setI18nLoaded(true);
            })
            .catch(error => {
                logger(logLevels.ERROR, { source: 'RootApp.js', action: logEvents.EXTERNAL_EVENT_CALLED, payload: { Message: 'i18n initialization failed', Exception: error } });
                setI18nError(error);
            });
    }, []);

    if (i18nError) {
        return (
            <div style={{ padding: '20px', textAlign: 'center', color: 'red' }}>
                <h1>Error Loading Application</h1>
                <p>Could not load essential resources. Please refresh the page.</p>
                <p>Details: {i18nError.message || 'Unknown error'}</p>
            </div>
        );
    }

    if (!i18nLoaded) {
        return null;
    }

    const appContent = (
        <Suspense fallback={<div className="loading" />}>
            <I18nextProvider i18n={i18next}>
                <BrowserRouter future={{ v7_startTransition: true, v7_relativeSplatPath: true }}>
                    <Routes>
                        <Route path="/:lang" element={<Root />}>
                            {/* Use imported NotFound component */}
                            <Route path="error-page" element={<ErrorPage />} />
                            <Route path="view/sso" element={<Sso />} />
                            <Route path="view/oauth" element={<ConnectOauth />} />
                            <Route path="view" element={<DataProvider />}>
                                <Route path="init" element={<Init />} />
                                <Route path="trial" element={<Trial />} />
                                <Route path="plans" element={<Plans />} />
                                <Route path="plans-antivirus" element={<PlansAntivirus />} />
                                <Route path="plans-optimizer" element={<PlansOptimizer />} />
                                <Route path="plans-vpn" element={<PlansVpn />} />
                                <Route path="plans-upsell" element={<PlansUpsell />} />
                                <Route path="plans-avpro-cb" element={<PlansUpsell />} />
                                <Route path="plans-trial" element={<PlansTrial />} />
                                <Route path="upsell" element={<Upsell />} />
                                <Route path="one-click-payment" element={<OneClickPayment />} />
                                <Route path="onboarding" element={<Onboarding />} />
                                <Route path="interstitial" element={<Interstitial />} />
                                <Route path="identity-assistant" element={<IdentityAssistant />} />
                                <Route path="store" element={<Store />} />
                                <Route path="registration-offer" element={<RegistrationOffer />} />
                                <Route path="*" element={<NotFound />} />
                                <Route path="issues" element={<Toaster />} />
                            </Route>
                            <Route path="faq/identity-assistant" element={<Faq />} />
                            <Route path="fallback" element={<Fallback />} />
                            <Route path="not-found" element={<NotFound />} />
                        </Route>
                        <Route path="*" element={<NotFound />} />
                    </Routes>
                </BrowserRouter>
            </I18nextProvider>
        </Suspense>
    );

    // Enable StrictMode only in development to avoid double-invocation
    // of effects and other dev-only checks in production builds.
    if (process.env.NODE_ENV === 'development') {
        return (
            <React.StrictMode>
                {appContent}
            </React.StrictMode>
        );
    }

    return appContent;
}

export default RootApp;
